<template>
  <div class="feedback-page">
    <div class="page-header">
      <div>
        <h2>AI 标签反馈清单</h2>
        <p>汇总运营发现的识别问题。处理反馈不会自动修改词表或 AI 规则。</p>
      </div>
      <div>
        <el-button :loading="exporting" @click="exportFeedbacks">导出 CSV</el-button>
        <el-button :loading="loading" @click="load">刷新</el-button>
      </div>
    </div>

    <div class="filters">
      <el-radio-group v-model="filters.status" @change="reload">
        <el-radio-button label="open">待处理</el-radio-button>
        <el-radio-button label="resolved">已处理</el-radio-button>
        <el-radio-button label="dismissed">不采纳</el-radio-button>
        <el-radio-button label="all">全部</el-radio-button>
      </el-radio-group>
      <el-select v-model="filters.issue_type" clearable placeholder="全部问题类型" style="width: 190px" @change="reload">
        <el-option v-for="item in issueTypes" :key="item.value" :label="item.label" :value="item.value" />
      </el-select>
    </div>

    <el-table v-loading="loading" :data="feedbacks" row-key="id">
      <el-table-column label="图片" width="130">
        <template #default="scope">
          <el-image :src="resourceUrl(scope.row.image_thumbnail || scope.row.image_resource)"
                    :preview-src-list="[resourceUrl(scope.row.image_resource)]" preview-teleported
                    fit="cover" style="width: 96px; height: 96px; border-radius: 6px" />
          <div class="image-id">{{ scope.row.image_id }}</div>
        </template>
      </el-table-column>
      <el-table-column label="问题" width="150">
        <template #default="scope">
          <el-tag :type="issueTagType(scope.row.issue_type)">{{ issueLabel(scope.row.issue_type) }}</el-tag>
          <div class="muted">{{ scope.row.created_at }}</div>
          <div class="muted">{{ scope.row.reporter || '未知提交人' }}</div>
        </template>
      </el-table-column>
      <el-table-column label="AI 原始结果" min-width="230">
        <template #default="scope">
          <div v-for="field in tagFields" :key="field.key" class="tag-line">
            <span class="field-name">{{ field.label }}：</span>{{ suggestedText(scope.row, field.key) }}
          </div>
        </template>
      </el-table-column>
      <el-table-column label="提交时的标签快照" min-width="230">
        <template #default="scope">
          <div v-for="field in tagFields" :key="field.key" class="tag-line">
            <span class="field-name">{{ field.label }}：</span>{{ expectedText(scope.row, field.key) }}
          </div>
        </template>
      </el-table-column>
      <el-table-column label="说明与模型" min-width="220">
        <template #default="scope">
          <div>{{ scope.row.note || '未填写说明' }}</div>
          <div class="muted">模型：{{ scope.row.model || '未知' }}</div>
          <div class="muted">词表版本：{{ scope.row.vocabulary_version || 0 }}</div>
        </template>
      </el-table-column>
      <el-table-column label="处理状态" width="210" fixed="right">
        <template #default="scope">
          <el-tag :type="statusType(scope.row.status)">{{ statusLabel(scope.row.status) }}</el-tag>
          <div v-if="scope.row.resolution" class="resolution">{{ scope.row.resolution }}</div>
          <div v-if="scope.row.resolver" class="muted">{{ scope.row.resolver }} · {{ scope.row.resolved_at }}</div>
          <div class="actions">
            <el-button v-if="scope.row.status === 'open'" size="small" type="success" @click="process(scope.row, 'resolved')">标记已处理</el-button>
            <el-button v-if="scope.row.status === 'open'" size="small" @click="process(scope.row, 'dismissed')">不采纳</el-button>
            <el-button v-else size="small" @click="reopen(scope.row)">重新打开</el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination v-model:current-page="page" :page-size="pageSize" :total="total"
                   layout="total, prev, pager, next" class="pagination" @current-change="load" />
  </div>
</template>

<script>
import axios from 'ts-axios-new';
import {ElMessage, ElMessageBox} from 'element-plus';

const CDN_URL = import.meta.env.VITE_CDN_URL || 'https://jigsaw-cdn.plutodesk.com';

export default {
  name: 'AiTagFeedback',
  data() {
    return {
      loading: false,
      exporting: false,
      feedbacks: [],
      total: 0,
      page: 1,
      pageSize: 20,
      filters: {status: 'open', issue_type: ''},
      categoryMap: {},
      majorMap: {},
      minorMap: {},
      assistantMap: {},
      issueTypes: [
        {value: 'wrong_tag', label: '标签错了'},
        {value: 'missing_tag', label: '缺少标签'},
        {value: 'extra_tag', label: '多余标签'},
        {value: 'wrong_category', label: '分类错误'},
        {value: 'vocabulary_missing', label: '词表没有合适标签'},
        {value: 'other', label: '其他'},
      ],
      tagFields: [
        {key: 'categories', label: '分类'},
        {key: 'major_tags', label: '主要'},
        {key: 'minor_tags', label: '次要'},
        {key: 'assistant_tags', label: '辅助'},
      ],
    };
  },
  methods: {
    resourceUrl(resource) {
      if (!resource || /^https?:\/\//.test(resource)) return resource || '';
      return `${CDN_URL.replace(/\/$/, '')}/${resource}`;
    },
    flattenTagMap(items, target) {
      (items || []).forEach(item => {
        target[item.id] = item.name;
        this.flattenTagMap(item.children, target);
      });
    },
    loadMaps() {
      return axios.all([
        axios.get('/cms/v1/category').then(res => {
          (res.data.data.categoryList || []).forEach(item => {
            this.categoryMap[item.id] = item.i18n_name?.ZH_HANS || item.key;
          });
        }),
        axios.get('/cms/v1/tag/major').then(res => this.flattenTagMap(res.data.data.tagList, this.majorMap)),
        axios.get('/cms/v1/tag/minor').then(res => this.flattenTagMap(res.data.data.tagList, this.minorMap)),
        axios.get('/cms/v1/tag/assistant').then(res => this.flattenTagMap(res.data.data.tagList, this.assistantMap)),
      ]);
    },
    load() {
      this.loading = true;
      return axios.get('/cms/v1/ai/tag/feedback', {params: {
        status: this.filters.status,
        issue_type: this.filters.issue_type || undefined,
        skip: (this.page - 1) * this.pageSize,
        limit: this.pageSize,
      }}).then(res => {
        this.feedbacks = res.data.data.feedbackList || [];
        this.total = res.data.data.total || 0;
      }).catch(error => {
        ElMessage.error(error?.response?.data?.status?.msg || '加载反馈清单失败');
      }).finally(() => {
        this.loading = false;
      });
    },
    reload() {
      this.page = 1;
      this.load();
    },
    issueLabel(value) {
      return this.issueTypes.find(item => item.value === value)?.label || value;
    },
    issueTagType(value) {
      return value === 'vocabulary_missing' ? 'warning' : (value === 'other' ? 'info' : 'danger');
    },
    statusLabel(value) {
      return {open: '待处理', resolved: '已处理', dismissed: '不采纳'}[value] || value;
    },
    statusType(value) {
      return {open: 'warning', resolved: 'success', dismissed: 'info'}[value] || 'info';
    },
    suggestedText(row, field) {
      const values = row.ai_suggested_fields?.[field] || [];
      const map = {
        categories: this.categoryMap,
        major_tags: this.majorMap,
        minor_tags: this.minorMap,
        assistant_tags: this.assistantMap,
      }[field];
      return values.length ? values.map(value => map[value] || value).join('、') : '未识别';
    },
    expectedText(row, field) {
      const values = row.expected_fields?.[field] || [];
      const map = {
        categories: this.categoryMap,
        major_tags: this.majorMap,
        minor_tags: this.minorMap,
        assistant_tags: this.assistantMap,
      }[field];
      return values.length ? values.map(value => map[value] || value).join('、') : '留空';
    },
    process(row, status) {
      const title = status === 'resolved' ? '标记已处理' : '标记不采纳';
      ElMessageBox.prompt('请填写处理结论，方便以后复盘。', title, {
        confirmButtonText: '确定', cancelButtonText: '取消', inputValue: row.resolution || '',
        inputValidator: value => Boolean(String(value || '').trim()) || '请填写处理结论',
      }).then(({value}) => this.updateStatus(row, status, value)).catch(() => {});
    },
    reopen(row) {
      ElMessageBox.confirm('确认将这条反馈重新设为待处理吗？', '重新打开', {
        confirmButtonText: '重新打开', cancelButtonText: '取消', type: 'warning',
      }).then(() => this.updateStatus(row, 'open', '')).catch(() => {});
    },
    updateStatus(row, status, resolution) {
      axios.put(`/cms/v1/ai/tag/feedback/${row.id}`, {status, resolution}).then(() => {
        ElMessage.success('反馈状态已更新');
        this.load();
      }).catch(error => {
        ElMessage.error(error?.response?.data?.status?.msg || '更新反馈失败');
      });
    },
    csvCell(value) {
      return `"${String(value ?? '').replace(/"/g, '""')}"`;
    },
    async exportFeedbacks() {
      this.exporting = true;
      try {
        const rows = [];
        let skip = 0;
        let total = 0;
        do {
          const res = await axios.get('/cms/v1/ai/tag/feedback', {params: {
            status: this.filters.status,
            issue_type: this.filters.issue_type || undefined,
            skip,
            limit: 100,
          }});
          const batch = res.data.data.feedbackList || [];
          total = res.data.data.total || 0;
          rows.push(...batch);
          skip += batch.length;
          if (!batch.length) break;
        } while (skip < total);

        const headers = ['图片ID', '问题类型', '说明', 'AI原始分类', 'AI原始主要标签', 'AI原始次要标签', 'AI原始辅助标签',
          '提交时分类', '提交时主要标签', '提交时次要标签', '提交时辅助标签', '模型', '词表版本', '提交人', '提交时间', '状态', '处理结论', '处理人', '处理时间'];
        const lines = [headers.map(this.csvCell).join(',')];
        rows.forEach(row => {
          lines.push([
            row.image_id, this.issueLabel(row.issue_type), row.note,
            this.suggestedText(row, 'categories'), this.suggestedText(row, 'major_tags'),
            this.suggestedText(row, 'minor_tags'), this.suggestedText(row, 'assistant_tags'),
            this.expectedText(row, 'categories'), this.expectedText(row, 'major_tags'),
            this.expectedText(row, 'minor_tags'), this.expectedText(row, 'assistant_tags'),
            row.model, row.vocabulary_version, row.reporter, row.created_at,
            this.statusLabel(row.status), row.resolution, row.resolver, row.resolved_at,
          ].map(this.csvCell).join(','));
        });
        const blob = new Blob([`\ufeff${lines.join('\n')}`], {type: 'text/csv;charset=utf-8'});
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `AI标签反馈-${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
        URL.revokeObjectURL(url);
        ElMessage.success(`已导出 ${rows.length} 条反馈`);
      } catch (error) {
        ElMessage.error(error?.response?.data?.status?.msg || '导出反馈失败');
      } finally {
        this.exporting = false;
      }
    },
  },
  mounted() {
    this.loading = true;
    this.loadMaps().then(() => this.load()).catch(() => {
      ElMessage.error('加载标签信息失败');
      this.loading = false;
    });
  },
};
</script>

<style scoped>
.feedback-page { padding: 20px; }
.page-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 18px; }
.page-header h2 { margin: 0 0 8px; }
.page-header p { margin: 0; color: var(--el-text-color-secondary); }
.filters { display: flex; gap: 12px; margin-bottom: 16px; }
.muted, .image-id { margin-top: 5px; color: var(--el-text-color-secondary); font-size: 12px; word-break: break-all; }
.tag-line { line-height: 1.7; }
.field-name { color: var(--el-text-color-secondary); }
.resolution { margin-top: 8px; line-height: 1.5; }
.actions { display: flex; gap: 6px; margin-top: 10px; flex-wrap: wrap; }
.pagination { margin-top: 18px; justify-content: flex-end; }
</style>
